import type { FC } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { useTranslate } from "@similarweb/i18n-provider";

import { Carousel } from "pro-features/components/Carousel/src/Carousel";
import { default as DurationService } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { DEVICES_TYPES_FOR_API, EDeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { CarouselWrapper, MetricsRow, MetricTitle } from "../StyledComponent";
import type { IResponseData, ISummaryTrendsData } from "../types";

import { AggregateMetrics } from "./aggregate-metrics/AggregateMetrics";
import { IntentDistribution } from "./intent-distribution/IntentDistribution";
import { TrafficDistributionContainer } from "./traffic-distribution/TrafficDistributionContainer";

const ENDPOINT_BASE = "api/WebsiteAnalysis/Overview";
const ENDPOINT_SUMMARY = `${ENDPOINT_BASE}/Summary`;
const ENDPOINT_SUMMARY_TRENDS = `${ENDPOINT_BASE}/SummaryTrends`;

export const Summary: FC = () => {
    const { translate } = useTranslate();
    const { params } = useCurrentStateAndParams();
    const { key, duration, country, webSource, pageFilter } = params;
    const { from, to, isWindow } = DurationService.getDurationData(duration as string).forAPI;

    const apiParams = {
        to,
        from,
        isWindow,
        country: country as number,
        webSource: webSource as EDeviceType,
        keys: key as string,
        includeSubDomains: true,
        device: (webSource === EDeviceType.MOBILE ? DEVICES_TYPES_FOR_API.MOBILE : webSource) as DEVICES_TYPES_FOR_API,
        pageFilterJson: pageFilter as object,
    };

    const { data: summaryData, isLoading } = useQuery({
        queryKey: [ENDPOINT_SUMMARY, to, from, isWindow, country, webSource, isWindow, key, pageFilter],
        queryFn: async () => DefaultFetchService.getInstance().get<IResponseData>(ENDPOINT_SUMMARY, apiParams),
    });
    const { data: trendsData } = useQuery({
        queryKey: [ENDPOINT_SUMMARY_TRENDS, to, isWindow, country, webSource, isWindow, key, pageFilter],
        queryFn: async () =>
            DefaultFetchService.getInstance().get<ISummaryTrendsData>(ENDPOINT_SUMMARY_TRENDS, apiParams),
    });
    return (
        <MetricsRow
            gap="16px"
            marginBottom="22px"
        >
            <MetricTitle>{translate("wa.traffic.sources.search.seo.overview.summary.title")}</MetricTitle>
            <CarouselWrapper>
                <Carousel margin={24}>
                    {[
                        <AggregateMetrics
                            trendsData={trendsData}
                            summaryData={summaryData}
                            isLoading={isLoading}
                        />,
                        <TrafficDistributionContainer
                            isLoading={isLoading}
                            data={summaryData?.TrafficDistribution}
                        />,
                        <IntentDistribution
                            isLoading={isLoading}
                            data={summaryData?.IntentDistribution}
                        />,
                    ]}
                </Carousel>
            </CarouselWrapper>
        </MetricsRow>
    );
};
